<template>
  <div class="order-mgmt purchase-mgmt-page">
    <div class="page-header">
      <div class="filter-card">
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">产品类型</span>
            <el-select v-model="params.productType" placeholder="全部" size="small" clearable filterable>
              <el-option v-for="product in productList" :key="product.productType" :label="product.productName" :value="product.productType" />
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">颜色</span>
            <el-select v-model="params.productColor" placeholder="全部" size="small" clearable>
              <el-option v-for="color in colorList" :key="color.key" :label="color.name" :value="color.key" />
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">型号</span>
            <el-select v-model="params.model" placeholder="全部" size="small" clearable filterable>
              <el-option v-for="item in modelList" :key="item" :label="item" :value="item" />
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">订单号</span>
            <el-input v-model="params.orderId" size="small" clearable placeholder="amazon-order-id" @keyup.enter.native="onQueryClick" />
          </div>
          <div class="filter-item">
            <span class="filter-label">ASIN</span>
            <el-input v-model="params.asin" size="small" clearable placeholder="asin" @keyup.enter.native="onQueryClick" />
          </div>
          <div class="filter-item">
            <span class="filter-label">SKU</span>
            <el-input v-model="params.sku" size="small" clearable placeholder="sku" @keyup.enter.native="onQueryClick" />
          </div>
          <div class="filter-item">
            <span class="filter-label">订单状态</span>
            <el-select v-model="params.orderStatus" placeholder="全部" size="small" clearable>
              <el-option v-for="item in orderStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">退货状态</span>
            <el-select v-model="params.returnStatus" placeholder="全部" size="small" clearable>
              <el-option v-for="item in returnStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">下单日期</span>
            <el-date-picker
              v-model="dateRange"
              size="small"
              type="daterange"
              align="right"
              unlink-panels
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              class="filter-date-wide"
            />
          </div>
          <div class="filter-actions">
            <el-button type="primary" size="small" icon="el-icon-search" @click="onQueryClick">查询</el-button>
            <el-button size="small" icon="el-icon-refresh-left" @click="onResetClick">重置</el-button>
            <el-button size="small" icon="el-icon-data-analysis" @click="goOrderAnalysis">订单分析</el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="table-panel" ref="tablePanel">
      <div class="table-container">
        <el-table
          :data="tableData"
          border
          stripe
          size="mini"
          :height="screenHeight"
          v-loading="loading"
          element-loading-text="加载中..."
          header-cell-class-name="table-header-cell"
          class="order-table"
        >
          <el-table-column label="序号" width="52" align="center" fixed>
            <template slot-scope="scope">{{ params.beginIndex + scope.$index + 1 }}</template>
          </el-table-column>
          <el-table-column prop="shopName" label="店铺" width="80" align="center" show-overflow-tooltip />
          <el-table-column label="订单号" width="160" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="mono-text">{{ scope.row["amazon-order-id"] || "—" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="订单状态" width="88" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="getOrderStatusType(scope.row['order-status'])" effect="light">
                {{ getOrderStatusName(scope.row["order-status"]) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="退货状态" width="84" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.returnStatus == 1" size="mini" type="danger" effect="plain">已退货</el-tag>
              <span v-else class="muted-text">—</span>
            </template>
          </el-table-column>
          <el-table-column prop="productName" label="产品名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="productColorName" label="颜色" width="72" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.productColorName || scope.row.productColor || "—" }}</template>
          </el-table-column>
          <el-table-column prop="model" label="型号" width="90" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.model || "—" }}</template>
          </el-table-column>
          <el-table-column prop="sku" label="SKU" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="mono-text">{{ scope.row.sku || "—" }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="asin" label="ASIN" width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="mono-text">{{ scope.row.asin || "—" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="价格" width="78" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span class="money-text">{{ formatPrice(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="currency" label="币种" width="56" align="center">
            <template slot-scope="scope">{{ scope.row.currency || "—" }}</template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="60" align="right" class-name="amount-col" />
          <el-table-column label="佣金" width="78" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span class="money-text">{{ formatNumber(scope.row.commission) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="采购成本" width="78" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span class="money-text">{{ formatNumber(scope.row.purchaseCost) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="头程" width="70" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span class="money-text">{{ formatNumber(scope.row.firstMileFee) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="毛利" width="84" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span class="money-text" :class="getGrossProfitClass(scope.row)">{{ getGrossProfit(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="下单时间" width="154" show-overflow-tooltip>
            <template slot-scope="scope">{{ getOrderTime(scope.row) }}</template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-box">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[20, 50, 100, 200]"
          :current-page.sync="currentPage"
          :page-size.sync="pageSize"
          :total="totalRecords"
          @size-change="onPageSizeChange"
          @current-change="loadList"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { requestModules } from "@r";
import { activeProducts, colors, models } from "../../utils/constant.js";

export default {
  name: "OrderMgmt",
  data() {
    return {
      productList: activeProducts,
      colorList: colors,
      modelList: models,
      orderStatusOptions: [
        { label: "未发货", value: "Unshipped" },
        { label: "已发货", value: "Shipped" },
        { label: "已取消", value: "Cancelled" },
      ],
      returnStatusOptions: [
        { label: "已退货", value: "1" },
        { label: "未退货", value: "0" },
      ],
      params: {
        productType: "",
        productColor: "",
        model: "",
        orderId: "",
        asin: "",
        sku: "",
        orderStatus: "",
        returnStatus: "",
        beginIndex: 0,
        pageSize: 50,
      },
      dateRange: [],
      pickerOptions: {
        shortcuts: [
          {
            text: "最近一周",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(end.getDate() - 6);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "最近一个月",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(end.getDate() - 29);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "最近三个月",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(end.getDate() - 89);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      tableData: [],
      currentPage: 1,
      pageSize: 50,
      totalRecords: 0,
      loading: false,
      screenHeight: 400,
    };
  },
  created() {
    this.initDefaultDateRange();
  },
  watch: {
    "params.productType": {
      handler(newValue) {
        if (!newValue) {
          this.colorList = colors;
          this.modelList = models;
          return;
        }
        const parentProduct = this.productList.find(pd => pd.productType == newValue);
        if (!parentProduct) {
          return;
        }
        const checkColors = Array.isArray(parentProduct.checkColors) ? parentProduct.checkColors : JSON.parse(parentProduct.checkColors || "[]");
        const checkModels = JSON.parse(parentProduct.checkModels || "[]");
        this.colorList = [];
        this.modelList = [...checkModels];
        colors.forEach(color => checkColors.includes(color.key) && this.colorList.push(color));
        this.params.productColor = "";
        this.params.model = "";
      },
    },
  },
  activated() {
    this.$nextTick(() => {
      this.calculateHeight();
      this.loadList();
    });
  },
  mounted() {
    this.calculateHeight();
    this.$eventBus.$on("resize", this.calculateHeight);
  },
  beforeDestroy() {
    this.$eventBus.$off("resize", this.calculateHeight);
  },
  methods: {
    initDefaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 89);
      this.dateRange = [start.format("yyyy-MM-dd"), end.format("yyyy-MM-dd")];
    },
    calculateHeight() {
      this.$nextTick(() => {
        const panel = this.$refs.tablePanel;
        if (!panel) {
          return;
        }
        const pagination = panel.querySelector(".pagination-box");
        const panelStyle = window.getComputedStyle(panel);
        const paddingTop = parseFloat(panelStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(panelStyle.paddingBottom) || 0;
        const paginationHeight = pagination ? pagination.offsetHeight : 0;
        const gap = 4;
        const availableHeight = panel.clientHeight - paddingTop - paddingBottom - paginationHeight - gap;
        this.screenHeight = Math.max(availableHeight, 200);
      });
    },
    formatNumber(value) {
      if (value === undefined || value === null || value === "") {
        return "—";
      }
      return Number(value).toFixed(2);
    },
    formatPrice(row) {
      const price = Number(row["item-price"]);
      if (isNaN(price)) {
        return "—";
      }
      return price.toFixed(2);
    },
    getOrderFbaFee(row) {
      const channel = row["sales-channel"];
      if (channel === "Amazon.ca") {
        return Number(row.caFbaFee ?? row.fbaFee) || 0;
      }
      if (channel === "Amazon.com.mx") {
        return Number(row.mxFbaFee ?? row.fbaFee) || 0;
      }
      return Number(row.fbaFee) || 0;
    },
    getGrossProfit(row) {
      if (row.grossProfit !== undefined && row.grossProfit !== null && row.grossProfit !== "") {
        return Number(row.grossProfit).toFixed(2);
      }
      const qty = Number(row.quantity) || 1;
      const itemPrice = Number(row["item-price"]);
      const purchaseCost = Number(row.purchaseCost) || 0;
      const firstMileFee = Number(row.firstMileFee) || 0;
      const fbaFee = this.getOrderFbaFee(row);
      if (isNaN(itemPrice) || itemPrice === 0) {
        return "—";
      }
      const unitPrice = itemPrice / qty;
      const profit = (unitPrice - unitPrice * 0.15 - fbaFee - purchaseCost - firstMileFee) * qty;
      return profit.toFixed(2);
    },
    getGrossProfitClass(row) {
      const text = this.getGrossProfit(row);
      if (text === "—") {
        return "";
      }
      const value = Number(text);
      if (isNaN(value)) {
        return "";
      }
      if (value > 0) {
        return "profit-positive";
      }
      if (value < 0) {
        return "profit-negative";
      }
      return "";
    },
    getOrderStatusName(status) {
      const statusMap = {
        Unshipped: "未发货",
        Shipped: "已发货",
        Cancelled: "已取消",
      };
      return statusMap[status] || status || "—";
    },
    getOrderStatusType(status) {
      if (status === "Shipped") {
        return "success";
      }
      if (status === "Unshipped") {
        return "warning";
      }
      if (status === "Cancelled") {
        return "info";
      }
      return "";
    },
    getOrderTime(row) {
      if (row.purchaseTime) {
        return row.purchaseTime.replace("_", " ");
      }
      if (row.purchaseDate) {
        return row.purchaseDate;
      }
      return row["purchase-date"] || "—";
    },
    onQueryClick() {
      this.currentPage = 1;
      this.loadList();
    },
    goOrderAnalysis() {
      const query = {};
      if (this.params.productType) {
        query.productType = this.params.productType;
      }
      this.$router.push({ path: "/client/orderMgmt/orderAnalysis", query });
    },
    onResetClick() {
      this.params.productType = "";
      this.params.productColor = "";
      this.params.model = "";
      this.params.orderId = "";
      this.params.asin = "";
      this.params.sku = "";
      this.params.orderStatus = "";
      this.params.returnStatus = "";
      this.initDefaultDateRange();
      this.colorList = colors;
      this.modelList = models;
      this.currentPage = 1;
      this.loadList();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.params.pageSize = this.pageSize;
      this.loadList();
    },
    buildQueryParams() {
      const query = {
        beginIndex: (this.currentPage - 1) * this.pageSize,
        pageSize: this.pageSize,
      };
      if (this.params.productType) {
        query.productType = this.params.productType;
      }
      if (this.params.productColor) {
        query.productColor = this.params.productColor;
      }
      if (this.params.model) {
        query.model = this.params.model;
      }
      if (this.params.orderId) {
        query["amazon-order-id"] = this.params.orderId.trim();
      }
      if (this.params.asin) {
        query.asin = this.params.asin.trim();
      }
      if (this.params.sku) {
        query.sku = this.params.sku.trim();
      }
      if (this.params.orderStatus) {
        query["order-status"] = this.params.orderStatus;
      }
      if (this.params.returnStatus === "1" || this.params.returnStatus === "0") {
        query.returnStatus = this.params.returnStatus;
      }
      if (this.dateRange && this.dateRange.length === 2) {
        query.beginDate = this.dateRange[0];
        query.endDate = this.dateRange[1];
      }
      return query;
    },
    loadList() {
      this.params.beginIndex = (this.currentPage - 1) * this.pageSize;
      this.params.pageSize = this.pageSize;
      this.loading = true;
      requestModules
        .fetchOrders(this.buildQueryParams())
        .then(res => {
          if (res.code !== 0) {
            this.$message.error((res && res.message) || "查询失败");
            return;
          }
          this.tableData = res.ldata || [];
          this.totalRecords = res.total || 0;
        })
        .finally(() => {
          this.loading = false;
          this.calculateHeight();
        });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/purchaseMgmtPage.scss";

.order-mgmt {
  .filter-item {
    .el-select,
    .el-input {
      width: 160px;
    }
  }

  .filter-date-wide {
    width: 260px;
  }

  .filter-actions {
    margin-bottom: 4px;
  }

  .order-table {
    width: 100%;

    .table-header-cell {
      background: #f5f7fa !important;
      color: #303133;
      font-weight: 600;
    }

    .amount-col .cell {
      font-variant-numeric: tabular-nums;
    }
  }

  .mono-text {
    font-family: Consolas, Menlo, Monaco, monospace;
    font-size: 12px;
    color: #303133;
  }

  .muted-text {
    color: #c0c4cc;
  }

  .money-text {
    color: #303133;

    &.profit-positive {
      color: #67c23a;
      font-weight: 600;
    }

    &.profit-negative {
      color: #f56c6c;
      font-weight: 600;
    }
  }
}
</style>
